import React, { useState } from 'react';
import { Loader2, AlertCircle } from 'lucide-react';
import { formatEther } from 'viem';
import { colors, spacing, typography, borders, shadows, transitions } from '../../../../tokens';
import { formatTokenAmount } from '../utils/formatters';
import type { LockConfig } from '../hooks/useContractReads';
import { Card } from '../../../../atoms';

interface PreviewData {
  previewLockMultiplier: bigint;
  previewTotalMultiplier: bigint;
  previewWeight: bigint;
  previewDailyRewards: bigint;
  previewAPY: bigint;
}

interface StakingFormProps {
  // Form state
  stakeAmount: string;
  lockDays: number;
  stakingStep: 'idle' | 'approving' | 'staking' | 'claiming' | 'unstaking' | 'extending';
  errorMessage: string | null;

  // Callbacks
  onStakeAmountChange: (amount: string) => void;
  onLockDaysChange: (days: number) => void;
  onApprove: () => void;
  onStake: () => void;

  // Data
  farBalance: bigint | undefined;
  lockConfig: LockConfig | undefined;
  needsApproval: boolean;
  isApproved: boolean;
  previewData: PreviewData | undefined;
  currentMultiplier: number;
}

export const StakingForm: React.FC<StakingFormProps> = ({
  stakeAmount,
  lockDays,
  stakingStep,
  errorMessage,
  onStakeAmountChange,
  onLockDaysChange,
  onApprove,
  onStake,
  farBalance,
  lockConfig,
  needsApproval,
  isApproved,
  previewData,
  currentMultiplier,
}) => {
  const minLockDays = lockConfig ? Number(lockConfig.minLockDays) : 0;
  const maxLockDays = lockConfig ? Number(lockConfig.maxLockDays) : 0;

  // Hover states for buttons
  const [isApproveHovered, setIsApproveHovered] = useState(false);
  const [isStakeHovered, setIsStakeHovered] = useState(false);

  return (
    <Card>
      <h3 style={{
        fontSize: typography.fontSize.xl,
        fontWeight: typography.fontWeight.semibold,
        color: colors.text.primary.default,
        margin: `0 0 ${spacing.lg} 0`,
      }}>
        Stake FAR Tokens
      </h3>

      {/* Amount Input */}
      <div style={{ marginBottom: spacing.lg }}>
        <label style={{
          display: 'block',
          fontSize: typography.fontSize.sm,
          fontWeight: typography.fontWeight.medium,
          color: colors.text.secondary.default,
          marginBottom: spacing.sm,
        }}>
          Amount to Stake
        </label>
        <div style={{
          display: 'flex',
          gap: spacing.sm,
        }}>
          <input
            type="number"
            value={stakeAmount}
            onChange={(e) => onStakeAmountChange(e.target.value)}
            placeholder="0.00"
            disabled={stakingStep !== 'idle'}
            style={{
              flex: 1,
              padding: spacing.md,
              fontSize: typography.fontSize.base,
              backgroundColor: colors.background.secondary.default,
              color: colors.text.primary.default,
              border: `1px solid ${colors.border.default.default}`,
              borderRadius: borders.component.default.radius,
              outline: 'none',
            }}
          />
          <button
            onClick={() => onStakeAmountChange(farBalance ? formatEther(farBalance) : '0')}
            disabled={stakingStep !== 'idle'}
            style={{
              padding: `${spacing.sm} ${spacing.md}`,
              fontSize: typography.fontSize.sm,
              fontWeight: typography.fontWeight.medium,
              backgroundColor: colors.background.secondary.default,
              color: colors.brand.cyan.base,
              border: `1px solid ${colors.border.default.default}`,
              borderRadius: borders.component.button.radius,
              cursor: stakingStep !== 'idle' ? 'not-allowed' : 'pointer',
            }}
          >
            MAX
          </button>
        </div>
        <div style={{
          fontSize: typography.fontSize.sm,
          color: colors.text.muted.default,
          marginTop: spacing.xs,
        }}>
          Available: {formatTokenAmount(farBalance)} FAR
        </div>
      </div>

      {/* Lock Period Slider */}
      <div style={{ marginBottom: spacing.xl }}>
        <label style={{
          display: 'block',
          fontSize: typography.fontSize.sm,
          fontWeight: typography.fontWeight.medium,
          color: colors.text.secondary.default,
          marginBottom: spacing.sm,
        }}>
          Lock Period: {lockDays} days
        </label>
        <input
          type="range"
          min={minLockDays ? minLockDays + 1 : 6}
          max={maxLockDays ? maxLockDays - 1 : 179}
          value={lockDays}
          onChange={(e) => onLockDaysChange(Number(e.target.value))}
          disabled={stakingStep !== 'idle'}
          style={{
            width: '100%',
            height: '8px',
            borderRadius: '4px',
            outline: 'none',
          }}
        />
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: spacing.xs,
        }}>
          <span style={{
            fontSize: typography.fontSize.sm,
            color: colors.text.muted.default,
          }}>
            {minLockDays > 0 ? `${minLockDays + 1} days` : '...'}
          </span>
          <span style={{
            fontSize: typography.fontSize.sm,
            fontWeight: typography.fontWeight.bold,
            color: colors.brand.purple.base,
          }}>
            {previewData ? (Number(previewData.previewLockMultiplier) / 1e18).toFixed(2) : currentMultiplier.toFixed(2)}x Multiplier
          </span>
          <span style={{
            fontSize: typography.fontSize.sm,
            color: colors.text.muted.default,
          }}>
            {maxLockDays > 0 ? `${maxLockDays - 1} days` : '...'}
          </span>
        </div>
      </div>

      {/* Position Preview */}
      {stakeAmount && parseFloat(stakeAmount) > 0 && (
        <div style={{
          backgroundColor: colors.background.secondary.default,
          padding: spacing.lg,
          borderRadius: borders.component.default.radius,
          marginBottom: spacing.lg,
          border: `1px solid ${colors.border.default.default}`,
        }}>
          <div style={{
            fontSize: typography.fontSize.sm,
            color: colors.text.secondary.default,
            marginBottom: spacing.md,
            fontWeight: typography.fontWeight.semibold,
          }}>
            Position Preview {!previewData && '(Loading...)'}
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: spacing.md,
          }}>
            <div>
              <div style={{
                fontSize: typography.fontSize.xs,
                color: colors.text.muted.default,
                marginBottom: spacing.xs,
              }}>
                Lock Multiplier
              </div>
              <div style={{
                fontSize: typography.fontSize.lg,
                fontWeight: typography.fontWeight.bold,
                color: colors.brand.orange.base,
              }}>
                {previewData ? (Number(previewData.previewLockMultiplier) / 1e18).toFixed(2) : '...'}x
              </div>
            </div>
            <div>
              <div style={{
                fontSize: typography.fontSize.xs,
                color: colors.text.muted.default,
                marginBottom: spacing.xs,
              }}>
                Total Multiplier
              </div>
              <div style={{
                fontSize: typography.fontSize.lg,
                fontWeight: typography.fontWeight.bold,
                color: colors.brand.purple.base,
              }}>
                {previewData ? (Number(previewData.previewTotalMultiplier) / 1e18).toFixed(2) : '...'}x
              </div>
            </div>
            <div>
              <div style={{
                fontSize: typography.fontSize.xs,
                color: colors.text.muted.default,
                marginBottom: spacing.xs,
              }}>
                Daily Rewards
              </div>
              <div style={{
                fontSize: typography.fontSize.lg,
                fontWeight: typography.fontWeight.bold,
                color: colors.semantic.success,
              }}>
                {previewData ? formatTokenAmount(previewData.previewDailyRewards) : '...'} FAR
              </div>
            </div>
            <div>
              <div style={{
                fontSize: typography.fontSize.xs,
                color: colors.text.muted.default,
                marginBottom: spacing.xs,
              }}>
                Estimated APY
              </div>
              <div style={{
                fontSize: typography.fontSize.lg,
                fontWeight: typography.fontWeight.bold,
                color: colors.brand.cyan.base,
              }}>
                {previewData ? ((Number(previewData.previewAPY) / 1e18) * 100).toFixed(2) : '...'}%
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div style={{
        display: 'flex',
        gap: spacing.md,
      }}>
        {needsApproval && (
          <button
            onClick={onApprove}
            disabled={stakingStep !== 'idle'}
            onMouseEnter={() => setIsApproveHovered(true)}
            onMouseLeave={() => setIsApproveHovered(false)}
            style={{
              flex: 1,
              padding: spacing.lg,
              backgroundColor: colors.brand.purple.base,
              color: colors.neutral.white,
              border: 'none',
              borderRadius: borders.component.button.radius,
              fontSize: typography.fontSize.base,
              fontWeight: typography.fontWeight.semibold,
              cursor: stakingStep !== 'idle' ? 'not-allowed' : 'pointer',
              opacity: stakingStep !== 'idle' ? 0.5 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: spacing.sm,
              boxShadow: (stakingStep === 'idle' && isApproveHovered)
                ? `${shadows.interactive.purpleHover}, ${shadows.elevation.medium}`
                : shadows.interactive.purple,
              transform: (stakingStep === 'idle' && isApproveHovered)
                ? 'translateY(-2px) scale(1.02)'
                : 'translateY(0) scale(1)',
              transition: transitions.button,
              backdropFilter: 'blur(8px)',
            }}
          >
            {stakingStep === 'approving' && <Loader2 size={20} className="spin" />}
            Approve FAR
          </button>
        )}
        <button
          onClick={onStake}
          disabled={!isApproved || stakingStep !== 'idle' || !stakeAmount || parseFloat(stakeAmount) <= 0}
          onMouseEnter={() => setIsStakeHovered(true)}
          onMouseLeave={() => setIsStakeHovered(false)}
          style={{
            flex: 1,
            padding: spacing.lg,
            backgroundColor: (!isApproved || stakingStep !== 'idle' || !stakeAmount || parseFloat(stakeAmount) <= 0)
              ? colors.brand.purple.base + '40'  // Purple with transparency for disabled state
              : colors.semantic.success,  // Green for active state (same as claim button)
            color: colors.neutral.white,
            border: (!isApproved || stakingStep !== 'idle' || !stakeAmount || parseFloat(stakeAmount) <= 0)
              ? `1px solid ${colors.brand.purple.base}`  // Purple border for disabled
              : `1px solid ${colors.semantic.success}`,  // Green border for active state
            borderRadius: borders.component.button.radius,
            fontSize: typography.fontSize.base,
            fontWeight: typography.fontWeight.semibold,
            cursor: (!isApproved || stakingStep !== 'idle' || !stakeAmount || parseFloat(stakeAmount) <= 0)
              ? 'not-allowed'
              : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: spacing.sm,
            boxShadow: (!isApproved || stakingStep !== 'idle' || !stakeAmount || parseFloat(stakeAmount) <= 0)
              ? shadows.glow.purple.md  // Purple glow for disabled
              : (isStakeHovered ? `${shadows.glow.green.lg}, ${shadows.elevation.medium}` : shadows.glow.green.md),  // Green glow for active
            transform: (isApproved && stakingStep === 'idle' && stakeAmount && parseFloat(stakeAmount) > 0 && isStakeHovered)
              ? 'translateY(-2px) scale(1.02)'
              : 'translateY(0) scale(1)',
            transition: transitions.button,
            backdropFilter: 'blur(8px)',
          }}
        >
          {stakingStep === 'staking' && <Loader2 size={20} className="spin" />}
          Stake {lockDays} Days
        </button>
      </div>

      {/* Error Message */}
      {errorMessage && (
        <div style={{
          marginTop: spacing.md,
          padding: spacing.md,
          backgroundColor: colors.semantic.error + '20',
          borderRadius: borders.component.default.radius,
          display: 'flex',
          alignItems: 'center',
          gap: spacing.sm,
        }}>
          <AlertCircle size={20} color={colors.semantic.error} />
          <span style={{
            fontSize: typography.fontSize.sm,
            color: colors.semantic.error,
          }}>
            {errorMessage}
          </span>
        </div>
      )}
    </Card>
  );
};
